/** @jsxRuntime classic */
/** @jsx jsx */
import { useRef, memo, forwardRef } from 'react';
import PropTypes from "prop-types";
import ReactDOM from 'react-dom';
import { jsx, css } from '@emotion/core';
const Sector = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  fill,
  strokeColor,
  strokeLinejoin,
  strokeWidth,
  onTouchStart,
  onTouchEnd,
  onMouseEnter,
  onMouseLeave,
  expandOnHover,
  expandedIndex,
  renderSelectedItem,
  className,
  path,
  name,
  quantity,
  href,
  transitionDuration,
  transitionTimingFunction,
  ...props
}, ref) => {
  let result;
  if (!ref) {
    ref = useRef(null);
  }
  const pathRef = useRef(null);
  const SectorCSS = expandOnHover || expandedIndex >= 0 ? css`
        transition-property: all;
        cursor: pointer;
        transition-timing-function: ${transitionTimingFunction};
        transition-duration: ${transitionDuration};
        &:hover {
            box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
            filter: drop-shadow( 0.05rem 0.05rem 0.1rem rgba(0, 0, 0, 0.2));
        }
    ` : css`
        transition-property: all;
        cursor: pointer;
        transition-timing-function: ${transitionTimingFunction};
        transition-duration: ${transitionDuration};
    `;
  const Data = (data, index) => {
    let item = null;
    if (renderSelectedItem && typeof renderSelectedItem === 'function') {
      item = renderSelectedItem({
        data,
        index
      });
      const el = document.createElement('title');
      ReactDOM.render(item, pathRef.current.appendChild(el));
    }
  };
  const content = jsx("g", {
    css: SectorCSS
  }, jsx("path", {
    d: path,
    fill: fill,
    ref: pathRef,
    stroke: strokeColor,
    className: className,
    strokeWidth: strokeWidth,
    strokeLinejoin: strokeLinejoin,
    onTouchStart: onTouchStart,
    onTouchEnd: onTouchEnd,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    ...props
  }, renderSelectedItem ? Data() : name && jsx("title", null, name, ": ", quantity)));
  if (href) {
    result = jsx("a", {
      href: href
    }, content);
  } else {
    result = content;
  }
  return result;
}));
Sector.defaultProps = {
  strokeColor: "#fff",
  strokeWidth: 0,
  strokeLinejoin: "round",
  name: null,
  quantity: null,
  href: null,
  transitionDuration: "8s",
  transitionTimingFunction: "ease-out"
};
Sector.propTypes = {
  /** the fill change color of pie chart  */
  fill: PropTypes.string.isRequired,
  /** event click mouse in pie chart */
  onMouseEnter: PropTypes.func,
  /** event leave mouse in pie chart */
  onMouseLeave: PropTypes.func,
  onTouchEnd: PropTypes.func,
  onTouchStart: PropTypes.func,
  /** function displays selected items by custom, example:<br/>
   * renderItem={(data, index) => index + ' - ' + data.name}<br/>
   */
  renderSelectedItem: PropTypes.func,
  path: PropTypes.string.isRequired,
  /** the StrokeColor change color border and Stroke */
  strokeColor: PropTypes.string,
  /** it can be applied to any element but it has effect only on the following nine */
  strokeLinejoin: PropTypes.string,
  /** The strokeWidth attribute is a presentation attribute defining the width of the stroke to be applied to the shape */
  strokeWidth: PropTypes.number,
  /** The title is add from the data */
  name: PropTypes.string,
  quantity: PropTypes.number,
  href: PropTypes.string,
  /** The transition-duration CSS property sets the length of time a transition animation should take to complete. Default 0.3s */
  transitionDuration: PropTypes.string,
  /** The transition-timing-function CSS property sets how intermediate values are calculated for CSS properties being affected*/
  transitionTimingFunction: PropTypes.string,
  className: PropTypes.string
};
export default Sector;